// @ts-expect-error — virtual module generated by vite-plugin-pwa at build time; not resolvable by tsc.
import { useRegisterSW } from 'virtual:pwa-register/react';
import { useEffect, useState } from 'react';
import { watchServiceWorkerUpdates } from './service-worker-updates.ts';

/**
 * Props for the pure presentation layer. In production, `ReloadPrompt`
 * reads these from `useRegisterSW`; in tests they can be injected
 * directly.
 */
export interface ReloadPromptViewProps {
  offlineReady: boolean;
  needRefresh: boolean;
  onReload(): void;
  onDismiss(): void;
}

/**
 * Stateless toast that renders the right message. Extracted so that
 * tests can drive it without mocking the PWA virtual module.
 */
export function ReloadPromptView({
  offlineReady,
  needRefresh,
  onReload,
  onDismiss,
}: ReloadPromptViewProps): JSX.Element | null {
  if (!offlineReady && !needRefresh) return null;

  return (
    <div
      data-testid="pwa-toast"
      className="fixed bottom-4 right-4 z-50 flex items-center gap-3 rounded-lg border border-[color:var(--app-border)] bg-[color:var(--app-panel-strong)] px-4 py-3 text-sm text-[color:var(--app-text)] shadow-lg"
    >
      {offlineReady && <span>Ready to work offline.</span>}
      {needRefresh && (
        <>
          <span>New version available.</span>
          <button type="button" onClick={onReload} className="ui-button ui-button-primary text-xs">
            Reload
          </button>
        </>
      )}
      <button
        type="button"
        aria-label="Dismiss"
        onClick={onDismiss}
        className="ml-1 text-[color:var(--app-text-soft)] hover:text-[color:var(--app-text-muted)]"
      >
        &times;
      </button>
    </div>
  );
}

/**
 * Production wrapper: reads PWA state from the service-worker
 * registration hook and delegates to the pure view.
 */
export function ReloadPrompt(): JSX.Element | null {
  const [registration, setRegistration] = useState<ServiceWorkerRegistration>();
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    offlineReady: [offlineReady, setOfflineReady],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_url: string, registered: ServiceWorkerRegistration | undefined) {
      setRegistration(registered);
    },
  });

  useEffect(() => {
    if (registration) return watchServiceWorkerUpdates(registration);
  }, [registration]);

  return (
    <ReloadPromptView
      offlineReady={offlineReady}
      needRefresh={needRefresh}
      onReload={() => updateServiceWorker(true)}
      onDismiss={() => {
        setOfflineReady(false);
        setNeedRefresh(false);
      }}
    />
  );
}
